import { type Bot } from '@/api/schema'
import { Row } from '@/app/components/Flex/Flex'
import { Link } from '@/app/components/Link/Link'
import { Text } from '@/app/components/Text/Text'
import { OnboardingType } from '@/app/const'
import CloseSvg from '@/app/icons/close.svg?react'
import evaPng from '@/app/img/layout/eva.png'
import milanaPng from '@/app/img/layout/milana.png'
import zoePng from '@/app/img/layout/zoe.png'
import { blockPage, unblockPage } from '@/app/utils/page'
import classNames from 'classnames'
import type { CSSProperties, FunctionalComponent } from 'preact'
import { createPortal } from 'preact/compat'
import { useEffect, useRef } from 'preact/hooks'
import styles from './index.module.scss'

export type UIBadgeProps = {
  upper?: boolean
  type: 'violet' | 'pink' | 'orange' | 'ghost' | 'orangeGr' | 'pinkGr'
  size?: 's' | 'm'
  class?: string
}

export type UIPanelProps = {
  overflow?: 'hidden'
  padding?: '0'
  link?: string
  class?: string
  style?: CSSProperties
  hover?: boolean
  clickable?: boolean
  shadow?: boolean
  type?:
    | 'border'
    | 'glass'
    | 'accent'
    | 'default'
    | 'violet'
    | 'yellow'
    | 'pink'
    | 'message'
    | 'user'
    | 'error'
    | 'transparent'
    | 'ghost'
    | 'holder'
  radius?: 'xs' | 's' | 'm' | 'l'
  onPointerLeave?: () => void
  onPointerEnter?: () => void
  onClick?: () => void
}

export const UI: FunctionalComponent & {
  Wrapper: FunctionalComponent<{
    class?: string
  }>
  Holder: FunctionalComponent<{
    maxWidth?: number
  }>
  Button: FunctionalComponent<{
    replace?: boolean
    class?: string
    gap?: number
    square?: boolean
    paddingRight?: number
    height?: number
    fontWeight?: number
    disabled?: boolean
    block?: boolean
    type: 'primary' | 'secondary' | 'white' | 'whiteSecondary' | 'light'
    size?: 'xs' | 's' | 'm' | 'l'
    htmlType?: 'button' | 'submit'
    link?: string
    onClick?: () => void
  }>
  Hint: FunctionalComponent
  Panel: FunctionalComponent<UIPanelProps>
  Badge: FunctionalComponent<UIBadgeProps>
  Icon: FunctionalComponent<{
    class?: string
    link?: string
    onClick?: () => void
  }>
  Avatar: FunctionalComponent<{
    ios?: boolean
    bot?: Bot | OnboardingType
    label?: boolean
    size?: 40 | 64 | 70
    sizeMobile?: 40 | 64 | 70
    radius?: 20
  }>
  Popup: FunctionalComponent<{
    onClose?: () => void
  }>
  Tabs: FunctionalComponent
  Tab: FunctionalComponent<{
    height?: number
    active?: boolean
    onClick?: () => void
  }>
} = () => {
  return (
    <div class={styles.root}>
    </div>
  )
}

UI.Wrapper = props => (
  <div class={classNames(styles.wrapper, props.class)}>
    {props.children}
  </div>
)

UI.Holder = props => (
  <div
    class={styles.holder}
    style={{
      maxWidth: props.maxWidth ? `${props.maxWidth}px` : undefined,
    }}
  >
    {props.children}
  </div>
)

UI.Button = props => {
  const Component = (props.link ? Link : 'button') as any

  return (
    <Component
      replace={props.replace}
      to={props.link}
      disabled={props.disabled}
      type={props.htmlType ?? 'button'}
      class={classNames(
        styles.btn,
        styles[props.type],
        styles[props.size ?? 'm'],
        {
          [styles.block]: props.block,
          [styles.square]: props.square,
        },
        props.class,
      )}
      style={{
        gap: props.gap ? `${props.gap}px` : undefined,
        paddingRight: props.paddingRight ? `${props.paddingRight}px` : undefined,
        fontWeight: props.fontWeight,
        height: props.height ? `${props.height}px` : undefined,
      }}
      onClick={props.onClick}
    >
      {props.children}
    </Component>
  )
}

UI.Panel = props => {
  const Component = (props.link ? Link : 'div') as any

  return (
    <Component
      to={props.link}
      class={classNames(
        styles.panel,
        styles[props.type ?? 'default'],
        styles[`br${props.radius ?? 'm'}`],
        styles[`p${props.padding ?? 'm'}`],
        {
          [styles.hover]: props.hover,
          [styles.clickable]: props.clickable,
          [styles.shadow]: props.shadow,
        },
        props.class,
      )}
      style={{
        ...props.style,
        overflow: props.overflow,
      }}
      onPointerLeave={props.onPointerLeave}
      onPointerEnter={props.onPointerEnter}
      onClick={props.onClick}
    >
      {props.children}
    </Component>
  )
}

UI.Hint = props => (
  <div class={styles.hint}>
    {props.children}
  </div>
)

UI.Badge = props => (
  <div
    class={classNames(
      styles.badge,
      styles[props.type],
      styles[props.size ?? 'm'],
      {
        [styles.upper]: props.upper,
      },
      props.class,
    )}
  >
    {props.children}
  </div>
)

UI.Avatar = props => {
  let src = zoePng
  let color = '#cf9ae6'

  const isEva = props.bot === 'eva'
    || props.bot === OnboardingType.Tarot

  const isMialana = props.bot === 'milana'
    || props.bot === OnboardingType.Matrix
    || props.bot === OnboardingType.MatrixMoney
    || props.bot === OnboardingType.MatrixLove
    || props.bot === OnboardingType.MatrixKarma

  if (isEva) {
    src = evaPng
    color = '#ffcaa2'
  }
  if (isMialana) {
    src = milanaPng
    color = '#fda6d8'
  }
  const img = (
    <img
      class={classNames(styles.avatar, {
        [styles.ios]: props.ios,
      })}
      src={src}
      alt=''
      style={{
        '--size-desktop': `${props.size ?? 70}px`,
        '--size-mobile': `${props.sizeMobile ?? props.size ?? 70}px`,
        borderRadius: props.radius ? `${props.radius}px` : undefined,
        background: color,
      }}
    />
  )
  return props.label
    ? (
      <Row
        gap={10}
        alignItems='center'
      >
        {img}
        <span class={styles.botLabel}>
          {isMialana
            ? 'Нумеролог Милана'
            : isEva
            ? 'Таролог Ева'
            : 'Астролог Зоя'}
        </span>
      </Row>
    )
    : img
}

UI.Icon = props => {
  const Component = (props.link ? Link : 'button') as any

  return (
    <Component
      type='button'
      class={classNames(styles.icon, props.class)}
      to={props.link}
      onClick={props.onClick}
    >
      {props.children}
    </Component>
  )
}

UI.Popup = props => {
  const popupRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    blockPage()

    return () => {
      unblockPage()
    }
  }, [])

  return createPortal(
    <div
      ref={popupRef}
      class={styles.popup}
      onClick={e => {
        if (e.currentTarget === e.target) {
          props.onClose?.()
        }
      }}
    >
      <div class={styles.inner}>
        {props.onClose && (
          <button
            type='button'
            class={styles.close}
            onClick={props.onClose}
          >
            <CloseSvg
              width={20}
              height={20}
              strokeWidth={1.5}
            />
          </button>
        )}

        <UI.Panel shadow>
          <Row gap={15}>
            {props.children}
          </Row>
        </UI.Panel>
      </div>
    </div>,
    document.body,
  )
}

UI.Tabs = props => (
  <div class={styles.tabs}>
    {props.children}
  </div>
)

UI.Tab = props => {
  const ref = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (props.active && ref.current) {
      scrollTabIntoView(ref.current)
    }
  }, [props.active])

  return (
    <button
      ref={ref}
      type='button'
      onClick={props.onClick}
      className={classNames(styles.tab, {
        [styles.active]: props.active,
      })}
      style={{
        height: props.height ? `${props.height}px` : undefined,
      }}
    >
      <Text
        upper
        as='span'
        fontSize={14}
        fontWeight={600}
      >
        {props.children}
      </Text>
    </button>
  )
}

function scrollTabIntoView(tab: HTMLElement) {
  const container = tab.parentElement

  if (!container) return

  container.scrollTo({
    left: tab.offsetLeft + tab.offsetWidth / 2 - container.clientWidth / 2,
    behavior: 'smooth',
  })
}
